import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { color } from '@/colors';
import { AppContextMenu } from '@/UI/AppContextMenu/AppContextMenu';
import { AppMenuItem } from '@/UI/AppContextMenu/AppMenu.styles';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { CheckMarkIcon } from '@/UI/Icons/CheckMarkIcon';
import ParentMenuItem from '@/components/Recipe/Menu/ParentMenuItem';
import { I18N_KEYS } from '@/language/keys';
import { FiltersIcon, XIcon } from '@/UI/Icons';
import { Tag } from '@/UI/Tag/Tag';
import { FlexCenVer } from '@/UI/styles';
import { EllipsisText } from '@/UI/EllipsisText/EllipsisText';

export type BooleanFilter = 'all' | true | false;

interface FilterOption<T> {
  value: T;
  label: string;
}

interface FilterCategoryConfig<T> {
  id: string;
  label: string;
  options: FilterOption<T>[];
  getCurrentValue: (props: ModelFiltersProps) => T;
  onChange: (props: ModelFiltersProps) => (value: T) => void;
}

interface ModelFiltersProps {
  allowedFilter: BooleanFilter;
  statusFilter: BooleanFilter;
  onAllowedFilterChange: (filter: BooleanFilter) => void;
  onStatusFilterChange: (filter: BooleanFilter) => void;
  hideStatusFilter?: boolean;
}

const getAllowedConfig = (t: (key: string) => string): FilterCategoryConfig<BooleanFilter> => ({
  id: 'allowed',
  label: t(I18N_KEYS.SETTINGS.MODEL_MANAGEMENT.FILTERS.ADMIN_APPROVAL.LABEL),
  options: [
    { value: 'all', label: t(I18N_KEYS.SETTINGS.MODEL_MANAGEMENT.FILTERS.ALL) },
    { value: true, label: t(I18N_KEYS.SETTINGS.MODEL_MANAGEMENT.FILTERS.ADMIN_APPROVAL.APPROVED) },
    { value: false, label: t(I18N_KEYS.SETTINGS.MODEL_MANAGEMENT.FILTERS.ADMIN_APPROVAL.NOT_APPROVED) },
  ],
  getCurrentValue: (props) => props.allowedFilter,
  onChange: (props) => props.onAllowedFilterChange,
});

const getStatusConfig = (t: (key: string) => string): FilterCategoryConfig<BooleanFilter> => ({
  id: 'status',
  label: t(I18N_KEYS.SETTINGS.MODEL_MANAGEMENT.FILTERS.STATUS.LABEL),
  options: [
    { value: 'all', label: t(I18N_KEYS.SETTINGS.MODEL_MANAGEMENT.FILTERS.ALL) },
    { value: true, label: t(I18N_KEYS.SETTINGS.MODEL_MANAGEMENT.FILTERS.STATUS.VERIFIED) },
    { value: false, label: t(I18N_KEYS.SETTINGS.MODEL_MANAGEMENT.FILTERS.STATUS.NOT_VERIFIED) },
  ],
  getCurrentValue: (props) => props.statusFilter,
  onChange: (props) => props.onStatusFilterChange,
});

export const ModelFilters = ({
  allowedFilter,
  statusFilter,
  onAllowedFilterChange,
  onStatusFilterChange,
  hideStatusFilter = false,
}: ModelFiltersProps) => {
  const props = {
    allowedFilter,
    statusFilter,
    onAllowedFilterChange,
    onStatusFilterChange,
    hideStatusFilter,
  };

  const { t } = useTranslation();
  const [filtersAnchorEl, setFiltersAnchorEl] = useState<null | HTMLElement>(null);

  const handleCloseMenu = () => setFiltersAnchorEl(null);

  const renderFilterCategory = <T,>(category: FilterCategoryConfig<T>) => {
    const currentValue = category.getCurrentValue(props);
    const onChange = category.onChange(props);

    return (
      <ParentMenuItem
        key={category.id}
        label={category.label}
        onClose={handleCloseMenu}
        id={category.id}
        contextMenuWidth="180px"
      >
        {category.options.map((option) => {
          const isSelected = currentValue === option.value;
          return (
            <AppMenuItem key={String(option.value)} selected={isSelected} onClick={() => onChange(option.value)}>
              <EllipsisText sx={{ flex: 1 }} variant="body-sm-rg">
                {option.label}
              </EllipsisText>
              {isSelected && <CheckMarkIcon />}
            </AppMenuItem>
          );
        })}
      </ParentMenuItem>
    );
  };

  const getSelectedFilterLabel = (filterValue: BooleanFilter, config: FilterCategoryConfig<BooleanFilter>) => {
    const option = config.options.find((opt) => opt.value === filterValue);
    return option?.label || '';
  };

  const allowedConfig = useMemo(() => getAllowedConfig(t), [t]);
  const statusConfig = useMemo(() => getStatusConfig(t), [t]);

  return (
    <>
      <ButtonContained
        mode="text"
        size="small"
        sx={{
          height: 34,
          mr: 1,
          ...(Boolean(filtersAnchorEl) && { backgroundColor: color.White08_T }),
        }}
        onClick={(e) => setFiltersAnchorEl(e.currentTarget)}
        startIcon={<FiltersIcon width={16} height={16} />}
      >
        {t(I18N_KEYS.GENERAL.FILTERS)}
      </ButtonContained>
      <FlexCenVer sx={{ gap: 1, flexWrap: 'wrap', flex: 1 }}>
        {allowedFilter !== 'all' && (
          <Tag
            text={getSelectedFilterLabel(allowedFilter, allowedConfig)}
            bgColor={color.Black84}
            textColor={color.White100}
            endIcon={<XIcon width={12} height={12} />}
            onClick={() => onAllowedFilterChange('all')}
            sx={{ cursor: 'pointer' }}
            variant="large"
          />
        )}
        {!hideStatusFilter && statusFilter !== 'all' && (
          <Tag
            text={getSelectedFilterLabel(statusFilter, statusConfig)}
            bgColor={color.Black84}
            textColor={color.White100}
            endIcon={<XIcon width={12} height={12} />}
            onClick={() => onStatusFilterChange('all')}
            sx={{ cursor: 'pointer' }}
            variant="large"
          />
        )}
      </FlexCenVer>

      <AppContextMenu
        anchorEl={filtersAnchorEl}
        open={Boolean(filtersAnchorEl)}
        onClose={handleCloseMenu}
        width="180px"
        offset={{ top: '4px' }}
      >
        {renderFilterCategory(allowedConfig)}
        {!hideStatusFilter && renderFilterCategory(statusConfig)}
      </AppContextMenu>
    </>
  );
};
